"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AXIS_STYLE, TOOLTIP_STYLE } from "@/lib/recharts-theme";
import type { StartupRecord } from "@/lib/types";

type Props = {
  startups: StartupRecord[];
};

type ChartPoint = {
  name: string;
  mrr: number;
  trustmrr_url: string;
  growth_30d: number | null;
};

export function StartupMrrChart({ startups }: Props) {
  const sorted = [...startups]
    .sort((a, b) => b.mrr - a.mrr)
    .slice(0, 15);

  if (sorted.length === 0) {
    return (
      <div className="chart-card">
        <h3 className="card-title">Top Startups by MRR</h3>
        <p className="card-desc">Highest revenue startups in this niche</p>
        <div className="chart-placeholder">No data to display</div>
      </div>
    );
  }

  const maxMrr = Math.max(...sorted.map((s) => s.mrr), 1);
  const data: ChartPoint[] = sorted.map((s) => ({
    name: s.name,
    mrr: s.mrr,
    trustmrr_url: s.trustmrr_url,
    growth_30d: s.growth_30d,
  }));

  const getBarFill = (_: unknown, index: number) => {
    if (index === 0) return "rgba(245, 158, 11, 0.8)";
    if (index === 1) return "rgba(148, 163, 184, 0.6)";
    if (index === 2) return "rgba(180, 83, 9, 0.6)";
    return "rgba(139, 92, 246, 0.5)";
  };

  const handleClick = (payload: ChartPoint) => {
    if (payload?.trustmrr_url) {
      window.open(payload.trustmrr_url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="chart-card">
      <h3 className="card-title">Top Startups by MRR</h3>
      <p className="card-desc">Highest revenue startups in this niche</p>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ right: 20, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
            <XAxis
              type="number"
              domain={[0, maxMrr * 1.1]}
              tick={AXIS_STYLE}
              tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={140}
              tick={AXIS_STYLE}
              tickLine={false}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              cursor={{ fill: "rgba(139, 92, 246, 0.1)" }}
              formatter={() => null}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload as ChartPoint;
                const growth =
                  p.growth_30d != null
                    ? ` · ${p.growth_30d > 0 ? "+" : ""}${p.growth_30d.toFixed(1)}% growth`
                    : "";
                return (
                  <div
                    style={{
                      ...TOOLTIP_STYLE.contentStyle,
                      padding: "12px 14px",
                    }}
                  >
                    <div style={TOOLTIP_STYLE.labelStyle}>
                      ${p.mrr.toLocaleString()}/mo{growth} · View on TrustMRR
                    </div>
                  </div>
                );
              }}
            />
            <Bar
              dataKey="mrr"
              radius={[0, 4, 4, 0]}
              maxBarSize={28}
              onClick={(item) => {
                const payload = item?.payload as ChartPoint | undefined;
                if (payload) handleClick(payload);
              }}
              cursor="pointer"
            >
              {data.map((_, index) => (
                <Cell key={index} fill={getBarFill(_, index)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
